<script setup lang="ts">
import { explainMonsterAffix } from '@/core/monster/affix'
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType, PersonMonsterType } from '@/types/monster'
import { socketEmitAsync } from '@/utils/web-socket'
import { dayjs, ElMessage } from 'element-plus'
import { ref, onMounted } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import GoldTempleRank from './GoldTempleRank.vue'
import { Warning } from '@element-plus/icons-vue'
import { goldTempleRank } from '../data'
import { useCommonStore } from '@/stores/modules/common'

const envMode = import.meta.env.VITE_ENV_MODE

const userStore = useUserStore()
const commonStore = useCommonStore()

defineProps<{ monster?: NestMonsterType }>()
const emits = defineEmits(['start', 'rankChange'])

const rank = ref(1)
const localKey = `war-soul-${envMode === 'test' ? 'test-' : ''}gold-temple-rank`
function handleChangeRank(val: number) {
  rank.value = val
  localStorage.setItem(localKey, String(rank.value))
  emits('rankChange', rank.value)
}
function handleStartGoldTemple() {
  const curTime = dayjs()
  const week = curTime.day()
  if (week === 6) {
    const hour = curTime.hour()
    if (hour === 23) {
      const min = curTime.minute()
      if (min >= 55) return ElMessage.warning('鎏金神殿结算中，无法进入挑战')
    }
  }
  if (userStore.goldTempleTimes <= 0) return ElMessage.warning('挑战次数已耗尽')

  ElMessageBox.confirm(`<p class="affix">确定进入【鎏金神殿】挑战吗？（不论成败都将<span class="shrink">消耗</span>挑战次数）</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync<PersonMonsterType>({ type: 'startGoldTemple', data: { rank: rank.value } })
      userStore.goldTempleTimes--
      emits('start')
    })
    .catch(() => {})
}

function handleSweep() {
  if (userStore.goldTempleRewardTimes <= 0) return ElMessage.warning('已无鎏金神殿奖励次数')
  ElMessageBox.confirm('<p class="affix">确定<span class="info">扫荡</span>鎏金神殿吗？</p>', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync({ type: 'sweepGoldTemple' })
      userStore.goldTempleRewardTimes--
      ElMessage.success('扫荡成功，请在邮件处领取奖励')
      useCommonStore().getMailList()
    })
    .catch(() => {})
}

const rankShow = ref(false)

onMounted(() => {
  const localRank = localStorage.getItem(localKey)
  if (localRank) rank.value = Number(localRank)
  emits('rankChange', rank.value)
})
</script>

<template>
  <div class="border-wrap gold-tower">
    <h4 class="myth">
      鎏金神殿
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>鎏金神殿说明</h4>
            <p>1. 鎏金神殿<span class="info">每周日0点刷新</span>，每周都将有一只史诗级生物登场，同时怪物属性随机。</p>
            <p>
              2. 您需要在<span class="info">5分钟</span>的时间内通过挑战，战斗时支持<span class="info">切换装备即刻生效</span
              >，在合适的时机切换装备是通关的关键。
            </p>
            <p>3. 您可以自由选择挑战难度，随着难度增加奖励也将增加，同时神话装备的超限概率以及圣物的品质爆率也将提高。</p>
            <p>4. 副本各项信息如表格所示：</p>
            <table border style="width: 100%; text-align: center">
              <tr>
                <td width="150">参考信息</td>
                <td class="normal">简单</td>
                <td class="race">普通</td>
                <td class="epic">困难</td>
                <td class="antiquity">噩梦</td>
              </tr>
              <tr>
                <td>最大超限可能</td>
                <td class="epic">3</td>
                <td class="legend">4</td>
                <td class="legend">4</td>
                <td class="legend">4</td>
              </tr>
              <tr>
                <td>精造石</td>
                <td>300</td>
                <td>500</td>
                <td>700</td>
                <td>1000</td>
              </tr>
              <tr>
                <td>奥秘锻造石掉率</td>
                <td>0%</td>
                <td>1%</td>
                <td>1.5%</td>
                <td>2%</td>
              </tr>
              <tr>
                <td>奖励圣物数目</td>
                <td>2</td>
                <td>3</td>
                <td>5</td>
                <td>8</td>
              </tr>
            </table>
          </div>
        </el-popover>
      </div>
    </h4>
    <p style="display: flex; align-items: center; color: red">
      <el-icon style="margin-right: 5px"><Warning /></el-icon>
      <span>终极副本，难度极高</span>
    </p>
    <p style="display: flex; align-items: center">
      难度：
      <el-select size="small" v-model="rank" :class="goldTempleRank[rank].class" style="width: 90px" @change="handleChangeRank">
        <el-option v-for="item of [1, 2, 3, 4]" :key="item" :label="goldTempleRank[item].name" :value="item">
          <span :class="goldTempleRank[item].class">{{ goldTempleRank[item].name }}</span>
        </el-option>
      </el-select>
    </p>
    <p>
      <span>剩余挑战次数：{{ userStore.goldTempleTimes }}</span>
    </p>
    <p>
      <span>剩余奖励次数：{{ userStore.goldTempleRewardTimes }}</span>
    </p>
    <p>
      本周怪物：
      <el-popover placement="bottom" :width="300" trigger="click" popper-class="monster-detail" :teleported="false" :disabled="!monster">
        <template #reference>
          <el-button size="small" type="danger" plain>
            <span v-show="monster">{{ monster?.name }}</span>
            <span v-show="!monster">暂无战斗</span>
          </el-button>
        </template>
        <div class="monster-detail">
          <h3 style="margin: 5px 0">{{ monster?.name }}</h3>
          <p>血量：{{ monster?.hp }}</p>
          <p>防御：{{ monster?.def }}</p>
          <p>闪避率：{{ monster?.evs }}%</p>
          <p>抗爆率：{{ monster?.crtdef }}%</p>
          <template v-if="monster?.special?.length">
            <p>特性：</p>
            <p v-for="item of explainMonsterAffix(monster.special)" v-html="item"></p>
          </template>
        </div>
      </el-popover>
    </p>
    <div style="margin: 10px 0">
      <el-button type="success" plain @click="handleStartGoldTemple()" :disabled="userStore.endlessTowerLevel < 20" v-show="userStore.soulType <= 3">
        {{ userStore.endlessTowerLevel < 20 ? '每周通过无尽之塔 20层后解锁' : '开始战斗' }}
      </el-button>
      <el-button color="#f7c99c" :dark="commonStore.isDarkMode" plain @click="handleSweep" v-show="userStore.soulType > 3">扫荡</el-button>
      <el-button type="primary" plain @click="rankShow = true">排行</el-button>
    </div>
  </div>

  <el-dialog v-model="rankShow" width="100%" style="max-width: 500px">
    <GoldTempleRank />
    <template #footer>
      <p style="text-align: center">
        <el-button plain @click="rankShow = false" size="small">关闭</el-button>
      </p>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.gold-tower {
  flex-flow: column;
}
:deep() {
  .normal {
    .el-select__selected-item {
      span {
        color: #888084;
      }
    }
  }
  .race {
    .el-select__selected-item {
      span {
        color: #3e62ed;
      }
    }
  }
  .epic {
    .el-select__selected-item {
      span {
        color: #8d4bbb;
      }
    }
  }
  .antiquity {
    .el-select__selected-item {
      span {
        color: #f05654;
      }
    }
  }
}
</style>
